import {Component, OnInit, ViewChild} from '@angular/core';
import {Grid, GridSearchItem} from '../../../../shared/Models/grid';
import {ActionColumn, ActionItem, TextColumn} from '../../../../shared/Models/columns';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {BroadcastService} from '../../../../shared/Services/broadcast.service';
import {ServiceRequestService} from '../../../Services/service-request.service';
import * as moment from 'moment';
import {AuthenticationService} from '../../../../shared/Services/authentication.service';
import {ServiceSubjectService} from '../../../Services/service-subject.service';
import {SolutionService} from '../../../Services/solution.service';
import {UsersService} from '../../../Services/users.service';
import {HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';

@Component({
  selector: 'app-service-request-overview',
  templateUrl: './service-request-overview.component.html',
  styleUrls: ['./service-request-overview.component.scss']
})
export class ServiceRequestOverviewComponent implements OnInit {

  public grid: Grid;
  public serviceRequestId;
  public serviceRequestName;
  @ViewChild('deleteModal', {static: false}) deleteModal = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthenticationService,
    private translateService: TranslateService,
    private broadcastService: BroadcastService,
    private serviceRequestService: ServiceRequestService,
    private serviceSubjectService: ServiceSubjectService,
    private solutionService: SolutionService,
    private userService: UsersService
  ) {
  }

  ngOnInit() {
    this.applyDefaultGridSort();
    const dateFormat = this.auth.getDateFormat();
    const serviceRequestService = this.serviceRequestService;
    this.grid = <Grid>{
      columns: [
        <TextColumn>{
          name: 'doctrine.service_request.overview.service_number',
          prop: 'serviceNumber',
          sortable: true,
          type: TextColumn.type,
          translate: true,
          valueFormatter: serviceNumber => serviceNumber ? serviceNumber : '-'
        },
        <TextColumn>{
          name: 'doctrine.attributes.dealer',
          prop: 'dealer',
          filterProp: 'user',
          type: TextColumn.type,
          sortable: true,
          translate: true,
          filterable: true,
          filterItems: this.getDealers(),
          filterMultiSelect: true,
          valueFormatter: dealer => dealer.companyName,
        },
        <TextColumn>{
          name: 'doctrine.service_request.overview.service_date',
          prop: 'createdAt',
          type: TextColumn.type,
          sortable: true,
          translate: true,
          valueFormatter: createdAt => moment(createdAt).format(dateFormat),
        },
        <TextColumn>{
          name: 'doctrine.service_request.subject',
          prop: 'serviceSubject',
          type: TextColumn.type,
          sortable: true,
          translate: true,
          filterable: true,
          filterItems: this.getServiceSubjects(),
          filterMultiSelect: true,
          valueFormatter: serviceSubject => serviceSubject ? serviceSubject.name : '-'
        },
        <TextColumn>{
          name: 'doctrine.solution.name',
          prop: 'reply',
          type: TextColumn.type,
          sortable: true,
          translate: true,
          filterable: true,
          filterItems: this.getSolutions(),
          filterMultiSelect: true,
          valueFormatter: reply => reply && reply.solution ? reply.solution.name : '-'
        },
        <TextColumn>{
          name: 'doctrine.service_request.current_step',
          prop: 'reply',
          type: TextColumn.type,
          sortable: false,
          translate: true,
          filterable: false,
          valueFormatter: (reply) => {
            const currentStepName = reply?.lastReplyItem?.solutionStep?.actionType?.name;
            const stepNameStr = currentStepName ? `doctrine.solution.action_type.${currentStepName}` : `doctrine.solution.action_type.no_action`;
            return this.translateService.instant(stepNameStr);
          }
        },
        <TextColumn>{
          name: 'global.status',
          prop: 'status',
          type: TextColumn.type,
          sortable: true,
          translate: true,
          filterable: true,
          filterItems: this.getStatuses(),
          filterMultiSelect: true,
          valueFormatter: status => status ? this.translateService.instant(status.translationKey) : ''
        },
        <ActionColumn>{
          name: 'global.actions',
          prop: null,
          type: ActionColumn.type,
          sortable: false,
          translate: true,
          actionItems: [
            <ActionItem>{
              icon: 'fa fa-eye fa-lg',
              tooltip: 'global.view',
              onclick: row => this.router.navigateByUrl('/back-office/service/request/' + row.id + '/show'),
              showCallback: row => row,
              btnClass: 'ripple light '
            },
            <ActionItem>{
              icon: 'fa fa-bell-o fa-lg',
              onclick: row => this.router.navigateByUrl('/back-office/service/request/' + row.id + '/show'),
              showCallback: row => !row.reply || !row.reply.backofficeIsRead,
              btnClass: 'ripple light '
            },
          ]
        }
      ],
      service: {
        getServiceRequests: (gridData = new HttpParams()) =>
          serviceRequestService.getServiceRequests(gridData).pipe(
            map(res => this.sortRowsWithUnreadFirst(res))
          )
      },
      rememberURL: this.router.url,
      method: 'getServiceRequests',
      defaultSort: 'createdAt',
      rowDetailShowCallback: (row, size) => size < 1400,
      root: 'service',
      defaultSortType: 'desc',
      showButton: true,
      doseNotHaveSearch: true,
      multipleRowSearch: false,
      search: [
        <GridSearchItem>{
          calendarFromDate: 'from',
          calendarToDate: 'to',
          hasCalendar: true,
          type: 'ng-select',
          hasDropdownListFilter: true,
          hasStatus: false,
          hasNormalSearch: true,
        }
      ],
    };
  }

  public deleteServiceRequest() {

  }

  private applyDefaultGridSort(): void {
    const storageKey = this.router.url;
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) {
      return;
    }
    const searchQuery = JSON.parse(stored);
    if (searchQuery.sortBy === 'position') {
      searchQuery.sortBy = 'createdAt';
      searchQuery.sortType = 'asc';
      sessionStorage.setItem(storageKey, JSON.stringify(searchQuery));
    }
  }

  private sortRowsWithUnreadFirst(res: any): any {
    if (!res?.data?.length) {
      return res;
    }
    res.data = [...res.data].sort((a, b) => {
      const aUnread = this.isUnreadServiceRequest(a.service);
      const bUnread = this.isUnreadServiceRequest(b.service);
      if (aUnread !== bUnread) {
        return aUnread ? -1 : 1;
      }
      return 0;
    });
    return res;
  }

  private isUnreadServiceRequest(row: any): boolean {
    return !row?.reply || !row.reply.backofficeIsRead;
  }

  private getDealers() {
    const options = [];
    this.userService.getAllDealerUsersDropdown({sortType: 'ASC', sortBy: 'companyName'}).subscribe((res) => {
      for (const row of res) {
        options.push({value: row.id, label: row.companyName});
      }
    });
    return options;
  }

  private getStatuses() {
    const statuses = [];
    this.serviceRequestService.getServiceRequestStatuses().subscribe(res => {
      for (const status of res.statuses) {
        statuses.push({value: status.id, label: this.translateService.instant(status.translationKey)});
      }
    });
    return statuses;
  }

  private getSolutions() {
    const solutions = [];
    this.solutionService.getSolutions().subscribe(res => {
      for (const row of res.data) {
        solutions.push({value: row.id, label: row.name});
      }
    });
    return solutions;
  }

  private getTypes() {
    return [
      {value: 0, label: this.translateService.instant('doctrine.service_request.type.service')},
      {value: 1, label: this.translateService.instant('doctrine.service_request.type.remark')}
    ];
  }

  private getServiceSubjects() {
    const serviceSubjects = [];
    this.serviceSubjectService.getServiceSubjects().subscribe(res => {
      for (const row of res.data) {
        serviceSubjects.push({value: row.id, label: row.name});
      }
    });
    return serviceSubjects;
  }
}
